import { useNavigate } from 'react-router-dom'
import { convertToShorthand } from '../../helpers/convert_date'
import { ButtonDropdown, Table, Icon } from '@bitcine/cinesend-theme'
import useStore from '../../hooks/use_store'
import { statusColors } from '../../constants/status_colour_map'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useTableFilters from '../../hooks/use_table_filters'
import { useState } from 'react'
import EditModal from './edit_modal'
import DeleteConfirmationModal from './delete_confirmation_modal'
import SchemaMappingModal from './schema_mapping_modal'
import { CycleStatus } from '../../constants/cycle_statuses'

const CyclesTable = ({ queryKey, airlines = null }) => {
  const [updatingCycleID, setUpdatingCycleID] = useState(null)
  const queryClient = useQueryClient()
  const filteringOptions = airlines
    ? [
        {
          label: 'Airline',
          key: 'airline_id',
          type: 'checkboxes',
          options: airlines.map((a) => ({ label: a.name, key: a.id })),
        },
      ]
    : null

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions,
    enableSearch: airlines !== null,
  })
  const { data: cycles, isLoading, error } = useQuery([queryKey, queryString])
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [showSchemaMappingModalWithData, setShowSchemaMappingModalWithData] = useState(false)
  const [cycle, setCycle] = useState(null)
  const navigate = useNavigate()
  const { deleteModel, updateModel } = useStore()

  const deleteCycle = (cycleID) => {
    deleteModel.mutate({
      endpoint: 'cycles',
      id: cycleID,
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, queryString])
      },
    })
  }

  const deleteCycleConfirmed = (cycleID, airlineGroupCode = null) => {
    deleteModel.mutate({
      endpoint: 'cycles',
      id: cycleID,
      params: airlineGroupCode ? `?airline_group_code=${airlineGroupCode}` : '',
      shouldDelete: true, // Skip confirmation prompt
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, queryString])
        setShowDeleteConfirmationModal(false)
      },
      onError: (error) => {
        setShowDeleteConfirmationModal(true)
      },
    })
  }

  return (
    <>
      <Table
        status={{
          pending: isLoading,
          error: error,
          errorMessage: error?.message,
        }}
        widths={[120, 160, 160, 120, 120, 100, 90, 'auto', 55]}
        header={{
          columns: [
            { text: 'Name', key: 'name' },
            { text: 'Airline', key: 'airline_id' },
            { text: 'Description', key: 'cycle' },
            { text: 'Start', key: 'start_date' },
            { text: 'End', key: 'end_date' },
            { text: 'Counts', key: 'status_counts' },
            { text: 'Status', key: '' },
            { text: 'Schema', key: '' },
            { text: ' ' },
          ],
          searching,
          sorting,
          filtering,
        }}
        body={{
          data: cycles,
          row: {
            compact: true,
            spaced: true,
            updatingCycleID: updatingCycleID,
            disabled: ({ id }) => id === updatingCycleID,
            onClick: (event, data) => navigate(`/airlines/${data.airline_id}/cycles/${data.id}`),
            render: [
              (data) => data.name,
              (data) => data.airline_name,
              (data) => data.description,
              (data) => convertToShorthand(data.start_date),
              (data) => convertToShorthand(data.end_date),
              ({ status_counts, id }, _, { updatingCycleID }) =>
                updatingCycleID === id ? (
                  <div className='w-full'>
                    <Icon icon='autorenew' className='animate-spin' />
                  </div>
                ) : Object.values(status_counts ?? {}).reduce((sum, count) => sum + count, 0) ? (
                  <div className='flex flex-row font-medium space-x-2'>
                    <div className={`${statusColors.new} pr-2 border-r-2 border-gray-200`} title='New'>
                      {status_counts.new ?? 0}
                    </div>
                    <div className={`${statusColors.holdover} pr-2 border-r-2 border-gray-200`} title='Holdover'>
                      {status_counts.holdover ?? 0}
                    </div>
                    <div className={`${statusColors.expiring} pr-2 border-r-2 border-gray-200`} title='Expiring'>
                      {status_counts.expiring ?? 0}
                    </div>
                    <div className={`${statusColors.recalled} pr-2`} title='Recalled'>
                      {status_counts.recalled ?? 0}
                    </div>
                  </div>
                ) : (
                  ''
                ),

              (data) => data.status_label,
              (data) => data.schema.name,
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Edit',
                        disabled: data.is_locked,
                        onClick: () => {
                          setCycle(data)
                          setShowEditModal(true)
                        },
                      },
                      {
                        text: data.status === CycleStatus.Closed ? 'Re-Open Cycle' : 'Open Cycle',
                        disabled: data.status === CycleStatus.Open,
                        onClick: () => {
                          setUpdatingCycleID(data.id)
                          updateModel.mutate(
                            {
                              endpoint: 'cycles',
                              id: `${data.id}/open${data.status === CycleStatus.Closed ? '?reopen' : ''}`,
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries([queryKey, queryString])
                              },
                              onSettled: () => setUpdatingCycleID(null),
                              onError: (error) => {
                                if (error.response?.data?.data !== null) {
                                  const mappingData = error.response?.data?.data ?? []
                                  if (Object.keys(mappingData).includes('fieldMappings')) {
                                    setCycle(data)
                                    setShowSchemaMappingModalWithData(mappingData)
                                  }
                                } else {
                                  console.error('error opening cycle', error)
                                }
                              },
                            }
                          )
                        },
                      },
                      {
                        text: 'Close Cycle',
                        disabled: data.status !== CycleStatus.Open,
                        onClick: () => {
                          setUpdatingCycleID(data.id)
                          updateModel.mutate(
                            { endpoint: 'cycles', id: `${data.id}/close` },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries([queryKey, queryString])
                              },
                              onSettled: () => setUpdatingCycleID(null),
                              onError: (error) => {
                                console.error('error closing cycle', error)
                              },
                            }
                          )
                        },
                      },
                      {
                        text: 'Delete',
                        className: 'text-red-500',
                        onClick: () => {
                          setCycle(data)
                          if (data.status === CycleStatus.New) {
                            deleteCycle(data.id)
                          } else {
                            setShowDeleteConfirmationModal(true)
                          }
                        },
                      },
                    ],
                  }}
                />
              ),
            ],
          },
          empty: {
            title: 'No cycles found!',
            text: 'Create a new cycle to begin.',
            icon: 'date_range',
          },
        }}
      />
      {showEditModal && (
        <EditModal
          cycle={cycle}
          invalidateKey={queryKey}
          onClose={() => setShowEditModal(false)}
          disabled={cycle.status === CycleStatus.Open}
        />
      )}
      {showSchemaMappingModalWithData !== false && (
        <SchemaMappingModal
          cycle={cycle}
          mappingData={showSchemaMappingModalWithData}
          invalidateKeys={[queryKey, queryString]}
          onClose={() => setShowSchemaMappingModalWithData(false)}
        />
      )}
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          cycle={cycle}
          errors={
            deleteModel?.error?.response?.data?.errors ? Object.values(deleteModel?.error?.response?.data.errors) : null
          }
          onClose={() => setShowDeleteConfirmationModal(false)}
          onConfirm={deleteCycleConfirmed}
        />
      )}
    </>
  )
}

export default CyclesTable
